// ✅ src/pages/VehicleV2/BillGenerationListing.jsx  (UPDATED FULL FILE)
// Mount assumed:
//   app.use("/api/billGenerationListing", billGenerationListing);
//
// Backend endpoints assumed:
//   GET  /api/billGenerationListing/dropdowns?branchId=&loginUserId=
//   GET  /api/billGenerationListing/rights?loginUserId=&pageName=Bill%20Generation%20Entry
//   GET  /api/billGenerationListing/list?branchId=&branchType=&loginUserId=&type=O|H&storeId=&vehTypeId=&vehSubTypeId=&vehBasis=&vehicleCode=&vehicleNumber=&vehicleName=
//   GET  /api/billGenerationListing/autocomplete/vehicleCode?prefix=&branchId=&branchType=&loginUserId=&type=&vehType=&subType=&vehBasis=&store=
//   GET  /api/billGenerationListing/autocomplete/vehicleNumber?prefix=&branchId=&branchType=&loginUserId=&type=&vehType=&subType=&store=
//   GET  /api/billGenerationListing/autocomplete/vehicleName?prefix=&branchId=&branchType=&loginUserId=&type=&vehType=&subType=&vehBasis=&store=
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + gridLoading + dropdownLoading + exporting states
// - cleaner filter / list / autocomplete handling
// - consistent card / section / button style
// - safer refresh / init / rights flow

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaFilter,
  FaFileExport,
  FaEye,
  FaSyncAlt,
  FaUndo,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/billGenerationListing";

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const clean = (v) => String(v ?? "").trim();

const wildcardOrValue = (v) => {
  const s = clean(v);
  if (!s || s === "Select" || s === "All") return "%";
  return s;
};

const defaultFilters = {
  storeId: "Select",
  vehTypeId: "Select",
  vehSubTypeId: "Select",
  vehBasis: "Select",
  vehicleCode: "",
  vehicleNumber: "",
  vehicleName: "",
};

const VehicleBillGenerationListing = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  /* ---------------- state ---------------- */
  const [vehicleOwnerType, setVehicleOwnerType] = useState("O"); // O/H

  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [hasRights, setHasRights] = useState(true);
  const [addRights, setAddRights] = useState(true);

  const [stores, setStores] = useState([{ id: "Select", name: "Select" }]);
  const [vehicleTypes, setVehicleTypes] = useState([
    { id: "Select", name: "Select" },
  ]);
  const [vehicleSubTypes, setVehicleSubTypes] = useState([
    { id: "Select", name: "Select" },
  ]);
  const [vehicleBasis, setVehicleBasis] = useState([
    { id: "Select", name: "Select" },
  ]);

  const [filters, setFilters] = useState(defaultFilters);
  const [showFilters, setShowFilters] = useState(true);

  const [list, setList] = useState([]);

  const [codeSuggestions, setCodeSuggestions] = useState([]);
  const [numSuggestions, setNumSuggestions] = useState([]);
  const [nameSuggestions, setNameSuggestions] = useState([]);

  const isBusy = pageLoading || dropdownLoading || gridLoading || exporting;

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  const suggestionBoxClass = useMemo(
    () =>
      `absolute z-20 mt-1 w-full rounded-lg border shadow-lg ${
        isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
      }`,
    [isDark]
  );

  /* ---------------- helpers ---------------- */
  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const resetSuggestions = useCallback(() => {
    setCodeSuggestions([]);
    setNumSuggestions([]);
    setNameSuggestions([]);
  }, []);

  const resetFiltersOnly = useCallback(() => {
    setFilters(defaultFilters);
    resetSuggestions();
  }, [resetSuggestions]);

  const buildListParams = useCallback(
    (override = {}) => ({
      branchId,
      branchType,
      loginUserId,
      type: vehicleOwnerType,
      storeId: override.storeId ?? filters.storeId,
      vehTypeId: override.vehTypeId ?? filters.vehTypeId,
      vehSubTypeId: override.vehSubTypeId ?? filters.vehSubTypeId,
      vehBasis: override.vehBasis ?? filters.vehBasis,
      vehicleCode: override.vehicleCode ?? filters.vehicleCode,
      vehicleNumber: override.vehicleNumber ?? filters.vehicleNumber,
      vehicleName: override.vehicleName ?? filters.vehicleName,
    }),
    [branchId, branchType, loginUserId, vehicleOwnerType, filters]
  );

  /* =========================================================
     API LOADERS
     ========================================================= */
  const loadRights = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/rights`, {
      params: {
        loginUserId,
        pageName: "Bill Generation Entry",
      },
      headers: commonHeaders,
    });

    if (!data?.success) {
      throw new Error(data?.message || "Failed to load rights");
    }

    setHasRights(!!data.hasRights);
    setAddRights(!!data.addRights);

    if (!data.hasRights) {
      toast.error("You don't have rights to access this page");
    }
  }, [loginUserId, commonHeaders]);

  const loadDropdowns = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/dropdowns`, {
      params: {
        branchId,
        loginUserId,
      },
      headers: commonHeaders,
    });

    if (!data?.success) {
      throw new Error(data?.message || "Failed to load dropdowns");
    }

    const makeOptions = (arr) => [
      { id: "Select", name: "Select" },
      ...(arr || []).map((x) => ({
        id: String(x.id),
        name: x.name,
      })),
    ];

    setStores(makeOptions(data.stores));
    setVehicleTypes(makeOptions(data.vehicleTypes));
    setVehicleSubTypes(makeOptions(data.vehicleSubTypes));
    setVehicleBasis(makeOptions(data.vehicleBasis));
  }, [branchId, loginUserId, commonHeaders]);

  const loadList = useCallback(
    async ({ silent = false, overrideFilters = null } = {}) => {
      if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
      if (!canUse(toInt(branchId), "branchId missing")) return;

      try {
        setGridLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(overrideFilters || {}),
          headers: commonHeaders,
        });

        if (!data?.success) {
          setList([]);
          if (!silent) {
            toast.warn(data?.message || "No data found");
          }
          return;
        }

        setList(data.list || []);

        if (!silent) {
          toast.success(`Found ${data.list?.length || 0} record(s)`);
        }
      } catch (err) {
        console.error(err);
        setList([]);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setGridLoading(false);
      }
    },
    [loginUserId, branchId, commonHeaders, buildListParams, canUse]
  );

  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;

    try {
      setPageLoading(true);

      await loadRights();
      await loadDropdowns();
      await loadList({ silent: true });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Initialization failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [loginUserId, branchId, canUse, loadRights, loadDropdowns, loadList]);

  const fetchSuggestions = useCallback(
    async (kind, prefix) => {
      const pfx = clean(prefix);
      if (!pfx) return [];

      const params = {
        prefix: pfx,
        branchId: branchType === "HO" ? "%" : branchId,
        branchType,
        loginUserId,
        type: vehicleOwnerType,
        vehType: wildcardOrValue(filters.vehTypeId),
        subType: wildcardOrValue(filters.vehSubTypeId),
        vehBasis: wildcardOrValue(filters.vehBasis),
        store: wildcardOrValue(filters.storeId),
      };

      let url = "";
      if (kind === "code") url = `${API_BASE}/autocomplete/vehicleCode`;
      if (kind === "number") url = `${API_BASE}/autocomplete/vehicleNumber`;
      if (kind === "name") url = `${API_BASE}/autocomplete/vehicleName`;

      if (!url) return [];

      const { data } = await axios.get(url, {
        params,
        headers: commonHeaders,
      });

      return data?.items || [];
    },
    [
      branchId,
      branchType,
      loginUserId,
      vehicleOwnerType,
      filters,
      commonHeaders,
    ]
  );

  /* ---------------- handlers ---------------- */
  const onFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  }, []);

  const handleSearch = useCallback(async () => {
    await loadList({ silent: false });
  }, [loadList]);

  const handleViewAll = useCallback(async () => {
    resetFiltersOnly();
    await loadList({
      silent: false,
      overrideFilters: defaultFilters,
    });
  }, [resetFiltersOnly, loadList]);

  const handleRefresh = useCallback(async () => {
    resetSuggestions();
    await loadInit();
  }, [resetSuggestions, loadInit]);

  const exportExcel = useCallback(async () => {
    try {
      setExporting(true);
      toast.info("Export feature can be connected to backend /export endpoint");
    } catch (err) {
      toast.error(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }, []);

  const openBillGeneration = useCallback(
    (vehId) => {
      navigate(`/vehicle/BillGeneration?VehID=${vehId}&brtype=${vehicleOwnerType}`);
    },
    [navigate, vehicleOwnerType]
  );

  const handleSuggestionInput = useCallback(
    async (kind, value) => {
      if (kind === "code") {
        setFilters((p) => ({ ...p, vehicleCode: value }));
      } else if (kind === "number") {
        setFilters((p) => ({ ...p, vehicleNumber: value }));
      } else if (kind === "name") {
        setFilters((p) => ({ ...p, vehicleName: value }));
      }

      if (value.length < 2) {
        if (kind === "code") setCodeSuggestions([]);
        if (kind === "number") setNumSuggestions([]);
        if (kind === "name") setNameSuggestions([]);
        return;
      }

      try {
        const items = await fetchSuggestions(kind, value);
        const topItems = items.slice(0, 8);

        if (kind === "code") setCodeSuggestions(topItems);
        if (kind === "number") setNumSuggestions(topItems);
        if (kind === "name") setNameSuggestions(topItems);
      } catch {
        if (kind === "code") setCodeSuggestions([]);
        if (kind === "number") setNumSuggestions([]);
        if (kind === "name") setNameSuggestions([]);
      }
    },
    [fetchSuggestions]
  );

  const selectSuggestion = useCallback(
    (kind, value) => {
      if (kind === "code") {
        setFilters((p) => ({ ...p, vehicleCode: value }));
        setCodeSuggestions([]);
      } else if (kind === "number") {
        setFilters((p) => ({ ...p, vehicleNumber: value }));
        setNumSuggestions([]);
      } else if (kind === "name") {
        setFilters((p) => ({ ...p, vehicleName: value }));
        setNameSuggestions([]);
      }
    },
    []
  );

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    if (!hasRights) return;
    loadList({ silent: true });
  }, [vehicleOwnerType]); // eslint-disable-line react-hooks/exhaustive-deps

  const recordsCount = useMemo(() => list.length, [list]);

  if (!hasRights) {
    return (
      <VehicleLayoutWrapper>
        <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
          <HeaderBar
            title={<span className="font-dmserif text-3xl">BILL GENERATION</span>}
            setSkin={setSkin}
            currentSkin={skin}
          />
          <div className={cardClass}>
            You don&apos;t have rights to access this page.
          </div>
        </div>
      </VehicleLayoutWrapper>
    );
  }

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BILL GENERATION LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Bill Generation Listing
              </div>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {branchId || "-"} {" • "} User ID: {loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>
                  Records: {recordsCount}
                </span>
                <span className={pillClass}>
                  Vehicle Mode: {vehicleOwnerType === "O" ? "Own" : "Hire"}
                </span>
                <span className={pillClass}>
                  Branch Type: {branchType || "-"}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowFilters((s) => !s)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
                title="Refresh"
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={exportExcel}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark ? "bg-green-700 hover:bg-green-600 text-white" : "bg-green-600 hover:bg-green-700 text-white"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaFileExport className="w-4 h-4" />
                {exporting ? "Exporting..." : "Export"}
              </button>
            </div>
          </div>

          {/* Vehicle Type */}
          <div className={sectionClass}>
            <div className="mb-6">
              <label className={labelClass}>Vehicle Type</label>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value="O"
                    checked={vehicleOwnerType === "O"}
                    onChange={(e) => setVehicleOwnerType(e.target.value)}
                    className="form-radio text-blue-600"
                    disabled={isBusy}
                  />
                  <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                    Own Vehicle
                  </span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value="H"
                    checked={vehicleOwnerType === "H"}
                    onChange={(e) => setVehicleOwnerType(e.target.value)}
                    className="form-radio text-blue-600"
                    disabled={isBusy}
                  />
                  <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                    Hire Vehicle
                  </span>
                </label>
              </div>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className={sectionClass}>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FaFilter /> FILTRATION AREA
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Store */}
                  <div>
                    <label className={labelClass}>Store</label>
                    <select
                      name="storeId"
                      value={filters.storeId}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      {stores.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Vehicle Type */}
                  <div>
                    <label className={labelClass}>Vehicle Type</label>
                    <select
                      name="vehTypeId"
                      value={filters.vehTypeId}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      {vehicleTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Vehicle Sub Type */}
                  <div>
                    <label className={labelClass}>Vehicle Sub Type</label>
                    <select
                      name="vehSubTypeId"
                      value={filters.vehSubTypeId}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      {vehicleSubTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Vehicle Basis */}
                  <div>
                    <label className={labelClass}>Vehicle Basis</label>
                    <select
                      name="vehBasis"
                      value={filters.vehBasis}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      {vehicleBasis.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Vehicle Code */}
                  <div className="relative">
                    <label className={labelClass}>Vehicle Code</label>
                    <input
                      type="text"
                      name="vehicleCode"
                      value={filters.vehicleCode}
                      onChange={(e) => handleSuggestionInput("code", e.target.value)}
                      className={inputClass}
                      placeholder="Enter vehicle code"
                      disabled={isBusy}
                      autoComplete="off"
                    />
                    {codeSuggestions.length > 0 && (
                      <div className={suggestionBoxClass}>
                        {codeSuggestions.map((s, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`w-full text-left px-3 py-2 text-sm ${
                              isDark
                                ? "hover:bg-gray-800 text-gray-200"
                                : "hover:bg-gray-100 text-gray-800"
                            }`}
                            onClick={() => selectSuggestion("code", s)}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Vehicle Number */}
                  <div className="relative">
                    <label className={labelClass}>Vehicle Number</label>
                    <input
                      type="text"
                      name="vehicleNumber"
                      value={filters.vehicleNumber}
                      onChange={(e) =>
                        handleSuggestionInput("number", e.target.value)
                      }
                      className={inputClass}
                      placeholder="Enter vehicle number"
                      disabled={isBusy}
                      autoComplete="off"
                    />
                    {numSuggestions.length > 0 && (
                      <div className={suggestionBoxClass}>
                        {numSuggestions.map((s, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`w-full text-left px-3 py-2 text-sm ${
                              isDark
                                ? "hover:bg-gray-800 text-gray-200"
                                : "hover:bg-gray-100 text-gray-800"
                            }`}
                            onClick={() => selectSuggestion("number", s)}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Vehicle Name */}
                  <div className="relative">
                    <label className={labelClass}>Vehicle Name</label>
                    <input
                      type="text"
                      name="vehicleName"
                      value={filters.vehicleName}
                      onChange={(e) => handleSuggestionInput("name", e.target.value)}
                      className={inputClass}
                      placeholder="Enter vehicle name"
                      disabled={isBusy}
                      autoComplete="off"
                    />
                    {nameSuggestions.length > 0 && (
                      <div className={suggestionBoxClass}>
                        {nameSuggestions.map((s, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`w-full text-left px-3 py-2 text-sm ${
                              isDark
                                ? "hover:bg-gray-800 text-gray-200"
                                : "hover:bg-gray-100 text-gray-800"
                            }`}
                            onClick={() => selectSuggestion("name", s)}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* action buttons */}
                <div className="flex flex-col md:flex-row gap-3 justify-between items-center mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={resetFiltersOnly}
                    disabled={isBusy}
                    className={`flex items-center gap-2 ${
                      isDark ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"
                    } font-medium transition-colors duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaUndo />
                    Clear Filters
                  </button>

                  <div className="flex gap-3">
                    <button
                      onClick={handleSearch}
                      disabled={isBusy}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
                    >
                      <FaSearch />
                      {gridLoading ? "Searching..." : "Search"}
                    </button>

                    <button
                      onClick={handleViewAll}
                      disabled={isBusy}
                      className="flex items-center gap-2 px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
                    >
                      View All
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* GRID */}
          <div className="mt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
              <div className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                Bill Generation List
              </div>
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Vehicle Mode:{" "}
                <span className="font-semibold">
                  {vehicleOwnerType === "O" ? "Own" : "Hire"}
                </span>
                {" • "}
                Records: <span className="font-semibold">{list.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead
                  className={`${buttonClass} text-white`}
                >
                  <tr>
                    <th className="px-4 py-3 text-center">S.No</th>
                    <th className="px-4 py-3 text-center">Vehicle Main Type</th>
                    <th className="px-4 py-3 text-center">Vehicle Code</th>
                    <th className="px-4 py-3 text-center">Vehicle Number</th>
                    <th className="px-4 py-3 text-center">Date</th>
                    <th className="px-4 py-3 text-center">Vehicle Name</th>
                    <th className="px-4 py-3 text-center">Vehicle Type</th>
                    <th className="px-4 py-3 text-center">Vehicle Basis</th>
                    <th className="px-4 py-3 text-center">Supplier</th>
                    <th className="px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>

                <tbody className={isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-800"}>
                  {list.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-10 text-center text-gray-500">
                        {gridLoading || pageLoading ? "Loading..." : "No records found"}
                      </td>
                    </tr>
                  ) : (
                    list.map((item, idx) => {
                      const vehicleIdToOpen =
                        item.id ?? item.vehicleId ?? item.vehId ?? "";

                      return (
                        <tr
                          key={`${vehicleIdToOpen}-${idx}`}
                          className={isDark ? "border-t border-gray-800" : "border-t border-gray-100"}
                        >
                          <td className="px-4 py-3 text-center">{idx + 1}</td>
                          <td className="px-4 py-3 text-center">{item.mainType || "-"}</td>
                          <td className="px-4 py-3 text-center">{item.code || "-"}</td>
                          <td className="px-4 py-3 text-center">{item.number || "-"}</td>
                          <td className="px-4 py-3 text-center">{item.date || "-"}</td>
                          <td className="px-4 py-3 text-center">{item.name || "-"}</td>
                          <td className="px-4 py-3 text-center">{item.type || "-"}</td>
                          <td className="px-4 py-3 text-center">{item.basis || "-"}</td>
                          <td className="px-4 py-3 text-center">{item.supplier || "-"}</td>

                          <td className="px-4 py-3 text-center">
                            <button
                              disabled={!addRights || isBusy}
                              onClick={() => openBillGeneration(vehicleIdToOpen)}
                              title={!addRights ? "No add rights" : "View / Generate Bill"}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg font-semibold transition-all ${
                                addRights
                                  ? `${buttonClass} ${hoverClass} text-white shadow`
                                  : "bg-gray-400 text-white cursor-not-allowed"
                              } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                            >
                              <FaEye />
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {isBusy && (
              <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {pageLoading
                  ? "Loading page..."
                  : dropdownLoading
                  ? "Loading dropdown..."
                  : gridLoading
                  ? "Loading grid..."
                  : exporting
                  ? "Exporting..."
                  : "Loading..."}
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleBillGenerationListing;